import i18n from '../i18n';
import { formatDate, formatDateTime, formatTime, toDateSafe } from './regionalFormat';

function minutesLabel(mins) {
  if (!mins || mins <= 0) return '';
  return i18n.t('common.minutesCount', { count: mins, defaultValue: '{{count}} minutes' });
}

/** Localize duration tokens like "60 minutes", "60m", "(60m)". */
export function localizeDurationLabel(raw) {
  if (!raw) return '';
  const s = String(raw).trim().replace(/^[(\[]|[)\]]$/g, '').trim();
  const m = s.match(/^(\d+)\s*(m|min|mins|minute|minutes)?$/i);
  if (m) return minutesLabel(Number(m[1]));
  return s;
}

/**
 * Parse backend session statement dateTime string, e.g.
 * "05/13/2026 05:00 PM - 06:00 PM (60m)" or legacy "(60 minutes)"
 */
export function parseSessionDateTimeString(dateTimeStr) {
  if (!dateTimeStr || dateTimeStr === '-') return null;
  const s = dateTimeStr.trim();

  const withDuration = s.match(/^(\d{1,2}[./-]\d{1,2}[./-]\d{4})\s+(.+)\s+\(([^)]+)\)$/);
  if (withDuration && withDuration[2].includes(' - ')) {
    return {
      date: withDuration[1],
      timeRange: withDuration[2].replace(/\s+/g, ' ').trim(),
      duration: localizeDurationLabel(withDuration[3]),
    };
  }

  const crossDay = s.match(/^(.+?)\s+(?:to|–)\s+(.+?)\s+\(([^)]+)\)$/i);
  if (crossDay) {
    const startFull = crossDay[1].trim();
    const endFull = crossDay[2].trim();
    const dateMatch = startFull.match(/^(\d{1,2}[./-]\d{1,2}[./-]\d{4})/);
    const startTimeOnly = dateMatch ? startFull.slice(dateMatch[0].length).trim() : startFull;
    const endTimeOnly = endFull.replace(/^\d{1,2}[./-]\d{1,2}[./-]\d{4}\s*/, '').trim();
    return {
      date: dateMatch ? dateMatch[1] : startFull,
      timeRange: `${startTimeOnly} - ${endTimeOnly}`,
      duration: localizeDurationLabel(crossDay[3]),
    };
  }

  return { date: s, timeRange: '', duration: '' };
}

/** Combine a session start timestamp with an API LocalTime (HH:mm or HH:mm:ss). */
export function combineSessionDateWithLocalTime(sessionStartTime, localTime) {
  if (!localTime) return null;
  const sessionStart = toDateSafe(sessionStartTime);
  if (!sessionStart) return null;
  const m = String(localTime).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const combined = new Date(sessionStart);
  combined.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return combined;
}

export function buildSessionDateTimeParts(startTime, endTime) {
  const start = toDateSafe(startTime);
  const end = toDateSafe(endTime);
  if (!start || !end) return null;

  const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (sameDay) {
    return {
      date: formatDate(start),
      timeRange: `${formatTime(start)} - ${formatTime(end)}`,
      duration: minutesLabel(mins),
    };
  }

  return {
    date: formatDate(start),
    timeRange: `${formatDateTime(start)} - ${formatDateTime(end)}`,
    duration: minutesLabel(mins),
  };
}

function parseDisplayTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const s = timeStr.trim();
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = parseInt(m12[2], 10);
    const pm = m12[3].toUpperCase() === 'PM';
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
    return h * 60 + min;
  }
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) return parseInt(m24[1], 10) * 60 + parseInt(m24[2], 10);
  return null;
}

function durationFromDisplayTimes(startTime, endTime) {
  const startM = parseDisplayTimeToMinutes(startTime);
  const endM = parseDisplayTimeToMinutes(endTime);
  if (startM == null || endM == null) return '';
  let diff = endM - startM;
  if (diff <= 0) diff += 24 * 60;
  return minutesLabel(diff);
}

export function sessionDateTimeFromParts(date, startTime, endTime) {
  if (!date) return null;
  const timeRange =
    startTime && endTime ? `${startTime} - ${endTime}` : startTime || endTime || '';
  return {
    date,
    timeRange,
    duration: durationFromDisplayTimes(startTime, endTime),
  };
}

export function resolveSessionDateTimeDisplay({
  dateTime,
  date,
  timeRange,
  duration,
  startTime,
  endTime,
}) {
  if (dateTime) return parseSessionDateTimeString(dateTime);
  if (startTime && endTime && !date && !timeRange) {
    return buildSessionDateTimeParts(startTime, endTime);
  }
  if (date && (startTime || endTime) && !timeRange) {
    return sessionDateTimeFromParts(date, startTime, endTime);
  }
  if (date || timeRange) {
    let derivedDuration = duration ? localizeDurationLabel(duration) : '';
    if (!derivedDuration && timeRange?.includes(' - ')) {
      const [startPart, endPart] = timeRange.split(' - ');
      derivedDuration = durationFromDisplayTimes(startPart?.trim(), endPart?.trim());
    }
    return {
      date: date || '',
      timeRange: timeRange || '',
      duration: derivedDuration,
    };
  }
  return null;
}
